import React, { useState, useEffect, useCallback } from 'react';
import {
  C, S, Badge, StatusBadge, FormField, ErrList, StatCard,
  PageHeader, FilterBar, DataTable, Modal, FormGrid,
  useToast, useConfirm, useCRUD,
} from '../../components/inventoryShared';
import {
  categoryController, subCategoryController, brandController,
  unitController, productController, warehouseController,
  locationController, supplierController, purchaseOrderController,
  purchaseReturnController, stockInController, stockOutController,
  stockTransferController, stockAdjustmentController,
  stockController, reportController,
} from '../../controllers/inventoryController';
import {
  Tag, Layers, Boxes, Settings, Package, BarChart3,
  PackageCheck, TrendingDown, ArrowLeftRight, ClipboardList,
  Users, ShoppingCart, RotateCcw, Warehouse, MapPin,
  AlertTriangle, Receipt, FileText, QrCode, Plus,
  Edit2, Trash2, Eye, Download, RefreshCw, CheckCircle,
  XCircle, Truck, ChevronDown, ChevronUp, Search,
} from 'lucide-react';
import ActionBtns from './ActionBtns';
const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
// ─────────────────────────────────────────────────────────────────────────────
// 8. SUPPLIERS PAGE
// ─────────────────────────────────────────────────────────────────────────────
export const SuppliersPage = ({ companyId }) => {
  const crud = useCRUD(supplierController, companyId);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [errors, setErrors]     = useState([]);
  const emptyForm = { name:'',code:'',contactPerson:'',phone:'',email:'',address:'',city:'',state:'',pincode:'',gstin:'',pan:'',bankName:'',accountNumber:'',ifscCode:'',paymentTerms:'Net 30',creditLimit:0,rating:0,status:'Active',notes:'' };
  const [form, setForm] = useState(emptyForm);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const openAdd  = () => { setEditItem(null); setForm(emptyForm); setErrors([]); setShowForm(true); };
  const openEdit = (r) => { setEditItem(r); setForm({...r}); setErrors([]); setShowForm(true); };
  const handleSave = async () => {
    setSaving(true); setErrors([]);
    const res = editItem ? await supplierController.update(editItem.id,{...form,companyId}) : await supplierController.create({...form,companyId});
    setSaving(false);
    if (res.success) { crud.show(res.message); setShowForm(false); crud.fetchAll(); }
    else setErrors(res.errors?.length?res.errors:[res.message]);
  };

  const ratingStars = (r) => '★'.repeat(r) + '☆'.repeat(5-r);
  const columns = [
    { label:'Supplier', key:'name', render:(v,r)=><div><div style={{ fontWeight:700 }}>{v}</div><div style={{ fontSize:11, color:C.textMuted }}>{r.contactPerson}</div></div> },
    { label:'Phone', key:'phone' },
    { label:'City', key:'city', render: v=>v||'—' },
    { label:'GSTIN', key:'gstin', render: v=><span style={{ fontFamily:'monospace', fontSize:12 }}>{v||'—'}</span> },
    { label:'Payment Terms', key:'paymentTerms', render: v=><Badge color={C.primary} bg={`${C.primary}12`}>{v||'—'}</Badge> },
    { label:'Outstanding', key:'outstanding', render: v=><span style={{ fontWeight:700, color: v>0 ? C.danger : C.textSoft }}>{money(v)}</span> },
    { label:'Rating', key:'rating', render: v=><span style={{ color:C.warning, fontSize:13 }}>{ratingStars(v||0)}</span> },
    { label:'Status', key:'status', render: v=><StatusBadge status={v}/> },
    { label:'', key:'actions', render:(_, r)=><ActionBtns onView={()=>setViewItem(r)} onEdit={()=>openEdit(r)} onDelete={()=>crud.handleDelete(r.id,`"${r.name}"`)} /> },
  ];

  return (
    <div style={S.page}>
      {crud.ToastEl}{crud.Dialog}
      <PageHeader title="Suppliers" subtitle="Manage suppliers and vendors" Icon={Users} color={C.purple}
        action={<button style={S.btnPri} onClick={openAdd}><Plus size={15}/> Add Supplier</button>} />
      <div style={S.card}>
        <div style={{ padding:'14px 14px 0' }}><FilterBar search={crud.search} onSearch={crud.setSearch} /></div>
        <DataTable columns={columns} data={crud.data} loading={crud.loading} emptyTitle="No suppliers found" onRowClick={setViewItem}
          page={crud.page} totalPages={crud.totalPages} onPageChange={crud.setPage} total={crud.total} />
      </div>
      {showForm && (
        <Modal title={editItem?'Edit Supplier':'Add Supplier'} onClose={()=>setShowForm(false)} maxWidth={800}
          footer={<><button style={S.btnSec} onClick={()=>setShowForm(false)}>Cancel</button><button style={S.btnPri} onClick={handleSave} disabled={saving}>{saving?'Saving...':'Save Supplier'}</button></>}>
          <ErrList errors={errors} />
          <div style={S.secTitle}>Basic Info</div>
          <FormGrid>
            <FormField label="Supplier Name *"><input style={S.input} value={form.name} onChange={e=>set('name',e.target.value)} /></FormField>
            <FormField label="Code"><input style={S.input} value={form.code} onChange={e=>set('code',e.target.value)} /></FormField>
            <FormField label="Contact Person"><input style={S.input} value={form.contactPerson} onChange={e=>set('contactPerson',e.target.value)} /></FormField>
            <FormField label="Phone *"><input style={S.input} value={form.phone} onChange={e=>set('phone',e.target.value)} /></FormField>
            <FormField label="Email"><input style={S.input} value={form.email} onChange={e=>set('email',e.target.value)} /></FormField>
            <FormField label="GSTIN"><input style={S.input} value={form.gstin} onChange={e=>set('gstin',e.target.value)} /></FormField>
            <FormField label="PAN"><input style={S.input} value={form.pan} onChange={e=>set('pan',e.target.value)} /></FormField>
            <FormField label="Rating (0-5)"><input type="number" min="0" max="5" style={S.input} value={form.rating} onChange={e=>set('rating',e.target.value)} /></FormField>
            <FormField label="Address" col={2}><input style={S.input} value={form.address} onChange={e=>set('address',e.target.value)} /></FormField>
            <FormField label="City"><input style={S.input} value={form.city} onChange={e=>set('city',e.target.value)} /></FormField>
            <FormField label="State"><input style={S.input} value={form.state} onChange={e=>set('state',e.target.value)} /></FormField>
            <FormField label="Pincode"><input style={S.input} value={form.pincode} onChange={e=>set('pincode',e.target.value)} /></FormField>
            <FormField label="Payment Terms"><select style={S.select} value={form.paymentTerms} onChange={e=>set('paymentTerms',e.target.value)}>{['Immediate','Net 7','Net 15','Net 30','Net 60','Net 90'].map(t=><option key={t}>{t}</option>)}</select></FormField>
            <FormField label="Credit Limit (₹)"><input type="number" style={S.input} value={form.creditLimit} onChange={e=>set('creditLimit',e.target.value)} /></FormField>
            <FormField label="Status"><select style={S.select} value={form.status} onChange={e=>set('status',e.target.value)}><option>Active</option><option>Inactive</option><option>Blacklisted</option></select></FormField>
          </FormGrid>
          <div style={S.secTitle}>Bank Details</div>
          <FormGrid>
            <FormField label="Bank Name"><input style={S.input} value={form.bankName} onChange={e=>set('bankName',e.target.value)} /></FormField>
            <FormField label="Account Number"><input style={S.input} value={form.accountNumber} onChange={e=>set('accountNumber',e.target.value)} /></FormField>
            <FormField label="IFSC Code"><input style={S.input} value={form.ifscCode} onChange={e=>set('ifscCode',e.target.value)} /></FormField>
          </FormGrid>
        </Modal>
      )}
      {viewItem && (
        <Modal title={viewItem.name} subtitle={`Code: ${viewItem.code||'—'}`} onClose={()=>setViewItem(null)} maxWidth={680}
          footer={<><button style={S.btnSec} onClick={()=>setViewItem(null)}>Close</button><button style={S.btnPri} onClick={()=>{setViewItem(null);openEdit(viewItem);}}>Edit</button></>}>
          <FormGrid>
            {[['Contact',viewItem.contactPerson||'—'],['Phone',viewItem.phone],['Email',viewItem.email||'—'],['GSTIN',viewItem.gstin||'—'],['PAN',viewItem.pan||'—'],['City / State',`${viewItem.city||''} ${viewItem.state||''}`.trim()||'—'],['Payment Terms',viewItem.paymentTerms],['Credit Limit',money(viewItem.creditLimit)],['Outstanding',money(viewItem.outstanding)],['Bank',viewItem.bankName||'—'],['Account',viewItem.accountNumber||'—'],['IFSC',viewItem.ifscCode||'—']].map(([l,v])=>(
              <div key={l} style={{ background:'#f9fafb', borderRadius:8, padding:'10px 13px' }}>
                <div style={{ fontSize:11, color:C.textMuted, fontWeight:700, textTransform:'uppercase', marginBottom:3 }}>{l}</div>
                <div style={{ fontSize:13, fontWeight:600 }}>{v}</div>
              </div>
            ))}
          </FormGrid>
        </Modal>
      )}
    </div>
  );
};
export default SuppliersPage